import { Injectable } from '@angular/core';
import { ReposeGroup } from 'icreate/common/models/repose-group';
import { getFooter, getPdfLayout, getTriggerCondition } from './print-repose-groups-utils';
import { TranslateService } from '@ngx-translate/core';
import { Channel } from 'icreate/common/models/channel';
import { lastValueFrom } from 'rxjs';
import { ReposeGroupService } from '@service/repose-group.service';
import { PrintDialogComponent, PrintPageType } from '@components/print-dialog/print-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

@Injectable({ providedIn: 'root' })
export class PrintReposeGroupService {
    constructor(
        private translateService: TranslateService,
        private reposeGroupService: ReposeGroupService,
        private dialog: MatDialog
    ) {}

    readonly NUM_ROWS_PAGE = 24;
    readonly rowsMaxPageHeight = 644;
    private DEFAULT_MARGIN = [2, 5, 5, 5];
    tableWidths = [30, 177, 177, 50, 50];

    async triggerPrint(vessel, comp: boolean = false): Promise<boolean> {
        let loadingRef = null;
        let blobUrl: string | undefined;
        let iframe: HTMLIFrameElement | undefined;

        try {
            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.REPOSE_GROUPS,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    showOptionsList: false,
                    notMain: comp,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;

            const { printType, version, iAPIShipyardlId } = shouldPrint;

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: {
                    progress: 0,
                },
            });

            loadingRef.componentInstance.data.progress = 10;

            const [pdfMake, pdfFonts, reposeGroups] = await Promise.all([
                import('pdfmake/build/pdfmake'),
                import('pdfmake/build/vfs_fonts'),
                this.reposeGroupService.getAllForPrint(vessel.id),
            ]);

            loadingRef.componentInstance.data.progress = 50;

            const pdfData = this.preparePdf(reposeGroups);

            loadingRef.componentInstance.data.progress = 85;

            const runtimeVfs = {
                ...pdfFonts.default?.pdfMake?.vfs,
            };

            const baseHref = window.location.origin + '/';

            const fonts = {
                MSGothic: {
                    normal: baseHref + 'assets/fonts/MSGothic.ttf',
                    bold: baseHref + 'assets/fonts/MSGothic.ttf',
                    italics: baseHref + 'assets/fonts/MSGothic.ttf',
                    bolditalics: baseHref + 'assets/fonts/MSGothic.ttf',
                },
            };

            const pdfDocGenerator = pdfMake.default.createPdf(pdfData, null, fonts, runtimeVfs);

            loadingRef.componentInstance.data.progress = 100;
            loadingRef.close();
            loadingRef = null;

            await new Promise<void>((resolve, reject) => {
                pdfDocGenerator.getBlob(blob => {
                    try {
                        const blobUrl = URL.createObjectURL(blob);
                        const newWindow = window.open(blobUrl, '_blank');
                        if (!newWindow) {
                            reject(new Error('Popup blocker prevented opening the new window.'));
                            return;
                        }
                        newWindow.onload = () => {
                            try {
                                newWindow.print();
                            } catch (e) {
                                console.error('Failed to trigger print dialog:', e);
                            }
                        };
                        resolve();

                        setTimeout(() => {
                            URL.revokeObjectURL(blobUrl);
                        }, 300000);
                    } catch (blobError) {
                        reject(blobError);
                    }
                });
            });
            return true;
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
        }
    }

    preparePdf(reposeGroups: (ReposeGroup & { channel: Channel })[]) {
        const pdfContent = this.generatePdfContent(reposeGroups);
        const pageFooter = { columns: getFooter(), margin: [290, 0, 0, 0] };

        return {
            pageSize: 'A4',
            footer: pageFooter,
            info: {
                title: 'repose-groups',
            },
            content: pdfContent,
            pageMargins: [66.693, 56.693, 58.347, 30],

            defaultStyle: {
                fontSize: 9,
                color: '#000000',
                font: 'MSGothic',
            },
            layout: {
                paddingLeft: function (i, node) {
                    return 0;
                },
                paddingRight: function (i, node) {
                    return 0;
                },
                paddingTop: function (i, node) {
                    return 0;
                },
                paddingBottom: function (i, node) {
                    return 0;
                },
                hLineWidth: (i, node) => {
                    return 0.75;
                },
                vLineWidth: () => {
                    return 0.75;
                },
                hLineColor: function (i, node) {
                    return 'black';
                },
                vLineColor: function () {
                    return 'black';
                },
            },
            background: function () {
                return [
                    {
                        canvas: [
                            {
                                type: 'line',
                                x1: 56.693,
                                y1: 56.693,
                                x2: 567.003,
                                y2: 56.693,
                                lineWidth: 1,
                            },
                            {
                                type: 'line',
                                x1: 56.693,
                                y1: 56.693,
                                x2: 56.693,
                                y2: 771.129,
                                lineWidth: 1,
                            },
                            {
                                type: 'line',
                                x1: 56.693,
                                y1: 771.129,
                                x2: 567.003,
                                y2: 771.129,
                                lineWidth: 1,
                            },
                            {
                                type: 'line',
                                x1: 567.003,
                                y1: 56.693,
                                x2: 567.003,
                                y2: 771.129,
                                lineWidth: 1,
                            },
                        ],
                    },
                ];
            },
        };
    }

    generatePdfContent(reposeGroups: (ReposeGroup & { channel: Channel })[]) {
        const tableBody = [];
        for (let i = 0; i < reposeGroups.length; i++) {
            const reposeGroup = reposeGroups[i];
            const reposeGroupRows = this.generateTableBody(reposeGroup);
            tableBody.push(...reposeGroupRows);
        }

        const pageRanges = this.getPageRanges(tableBody, this.tableWidths, this.rowsMaxPageHeight);
        const numOfPages = pageRanges.length;

        const pdfContent = [];
        const emptyRow = ['', '', '', '', ''];

        for (let i = 0; i < numOfPages; i++) {
            const title = this.translateService.instant('repose-group.page.print.title');
            const titleText = {
                text: title,
                font: 'MSGothic',
                fontSize: 12,
                alignment: 'center',
                margin: [5, 30, 0, 10],
            };
            const { first, last } = pageRanges[i];
            const pageRows = tableBody.slice(first, last + 1);

            let contentObject: { table: any; layout: any; pageBreak?: string } = {
                table: '',
                layout: getPdfLayout(),
                pageBreak: 'after',
            };

            if (i === numOfPages - 1) delete contentObject.pageBreak;
            const tableHeader = this.getTableHeader();

            let table = {
                widths: this.tableWidths,
                heights: function (row) {
                    if (row == 3) return 3;
                    return 20;
                },
                margin: [10, 20, 10, 20],
                body: [...tableHeader, emptyRow, ...pageRows],
            };

            contentObject.table = table;

            pdfContent.push(titleText);
            pdfContent.push(contentObject);
        }
        return pdfContent;
    }

    generateTableBody(reposeGroup: ReposeGroup & { channel: Channel }) {
        const startDelay = this.getDelayText(reposeGroup.startDelay);
        const stopDelay = this.getDelayText(reposeGroup.stopDelay);
        const body = [
            [
                {
                    rowSpan: 3,
                    text: reposeGroup.groupNo,
                    alignment: 'center',
                    margin: this.DEFAULT_MARGIN,
                },
                { text: reposeGroup.name, margin: this.DEFAULT_MARGIN, fontSize: 8 },
                {
                    text: reposeGroup.channel?.fcuData?.fcuChannelId || '',
                    margin: this.DEFAULT_MARGIN,
                },
                {
                    rowSpan: 3,
                    text: startDelay,
                    margin: this.DEFAULT_MARGIN,
                },
                {
                    rowSpan: 3,
                    text: stopDelay,
                    margin: this.DEFAULT_MARGIN,
                },
            ],
            [
                '',
                {
                    rowSpan: 2,
                    text: reposeGroup.description,
                    margin: this.DEFAULT_MARGIN,
                },
                {
                    text: reposeGroup.channel?.opsData?.name || '',
                    margin: this.DEFAULT_MARGIN,
                },
                '',
                '',
            ],
            [
                '',
                '',
                {
                    text: getTriggerCondition(reposeGroup, this.translateService),
                    margin: this.DEFAULT_MARGIN,
                    fontSize: 8,
                },
                '',
                '',
            ],
        ];
        return body;
    }

    getTableHeader() {
        const numText = this.translateService.instant('repose-group.page.table.header.num');
        const nameText = this.translateService.instant('repose-group.page.table.header.name.long');
        const descriptionText = this.translateService.instant('repose-group.page.table.header.description');
        const chTagText = this.translateService.instant('repose-group.page.table.header.trigger-tag');
        const chNameText = this.translateService.instant('repose-group.page.table.header.trigger-channel');
        const condText = this.translateService.instant('repose-group.page.table.header.trigger-condition');
        const delayOnText = this.translateService.instant('repose-group.page.table.header.delay-on');
        const delayOffText = this.translateService.instant('repose-group.page.table.header.delay-off');

        const rowHeader = [
            [
                {
                    rowSpan: 3,
                    text: numText,
                    alignment: 'center',
                    fillColor: '#d8d8d8',
                    verticalAlignment: 'middle',
                    margin: this.DEFAULT_MARGIN,
                },
                { text: nameText, margin: this.DEFAULT_MARGIN, fillColor: '#d8d8d8' },
                {
                    text: chTagText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
                {
                    rowSpan: 3,
                    text: delayOnText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
                {
                    rowSpan: 3,
                    text: delayOffText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
            ],
            [
                '',
                {
                    rowSpan: 2,
                    text: descriptionText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
                {
                    text: chNameText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
                '',
                '',
            ],
            [
                '',
                '',
                {
                    text: condText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
                '',
                '',
            ],
        ];

        return rowHeader;
    }

    getDelayText(delay) {
        if (delay) {
            const secondsText = this.translateService.instant('repose-group.page.print.seconds');
            return `${delay} ${secondsText}`;
        } else return this.translateService.instant('repose-group.page.print.no-delay');
    }

    estimateRowHeight(row: any[], widths: number[], fontSize = 9): number {
        const avgCharWidth = fontSize * 0.5;
        const lineHeight = fontSize * 1.4;
        const verticalPadding = 10;

        let maxLines = 1;

        row.forEach((cell, i) => {
            const text = typeof cell === 'object' ? cell.text : cell;
            if (!text) return;

            const colWidth = widths[i];
            const usableWidth = colWidth - 7;
            const charsPerLine = Math.max(1, Math.floor(usableWidth / avgCharWidth));
            const lines = Math.ceil(String(text).length / charsPerLine);

            maxLines = Math.max(maxLines, lines);
        });

        return maxLines * lineHeight + verticalPadding;
    }

    private getPageRanges(
        tableBody: any[],
        widths: number[],
        maxPageHeight: number
    ): { first: number; last: number }[] {
        const GROUP_SIZE = 3;
        const numGroups = Math.ceil(tableBody.length / GROUP_SIZE);

        const groupHeights: number[] = [];
        for (let g = 0; g < numGroups; g++) {
            const start = g * GROUP_SIZE;
            const groupRows = tableBody.slice(start, start + GROUP_SIZE);
            const height = groupRows.reduce((sum, row) => sum + this.estimateRowHeight(row, widths), 0);
            groupHeights.push(height);
        }

        const pages: { first: number; last: number }[] = [];
        let currentHeight = 0;
        let pageStartGroup = 0;

        for (let g = 0; g < numGroups; g++) {
            const groupHeight = groupHeights[g];

            if (currentHeight + groupHeight > maxPageHeight && g > pageStartGroup) {
                const first = pageStartGroup * GROUP_SIZE;
                const last = Math.min(g * GROUP_SIZE, tableBody.length) - 1;
                pages.push({ first, last });

                pageStartGroup = g;
                currentHeight = 0;
            }

            currentHeight += groupHeight;
        }

        if (pageStartGroup < numGroups) {
            const first = pageStartGroup * GROUP_SIZE;
            const last = tableBody.length - 1;
            pages.push({ first, last });
        }

        return pages;
    }
}
